import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  CreateBusinessInput,
  useCreateBusiness,
  useUpdateBusiness,
  useUploadBusinessDocuments,
  useUploadBusinessPhotos,
  useSetCoverPhoto,
} from "@/hooks/useBusinesses";
import { OwnerStep } from "./wizard/OwnerStep";
import { BasicsStep } from "./wizard/BasicsStep";
import { LocationStep } from "./wizard/LocationStep";
import { FacilityStep } from "./wizard/FacilityStep";
import { ImagesStep } from "./wizard/ImagesStep";
import { ComplianceStep } from "./wizard/ComplianceStep";
import {
  STEP_FIELDS,
  STEP_LABELS,
  WizardValues,
  defaultValues,
  wizardSchema,
} from "./wizard/schema";

export const BusinessCreateWizard = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const form = useForm<WizardValues>({
    resolver: zodResolver(wizardSchema),
    mode: "onBlur",
    defaultValues,
  });

  useEffect(() => {
    const ownerId = params.get("ownerId");
    if (ownerId) form.setValue("ownerId", ownerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = useCreateBusiness();
  const update = useUpdateBusiness();
  const uploadDocs = useUploadBusinessDocuments();
  const uploadPhotos = useUploadBusinessPhotos();
  const setCover = useSetCoverPhoto();

  const handleNext = async () => {
    const fields = STEP_FIELDS[step] as readonly (keyof WizardValues)[];
    const ok = fields.length === 0 ? true : await form.trigger(fields as any);
    if (!ok) return;
    setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
  };

  const isLast = step === STEP_LABELS.length - 1;

  const onSubmit = form.handleSubmit(async (values) => {
    if (!isLast) return;
    const {
      pendingDocuments = [],
      pendingPhotos = [],
      bankAccount,
      ...rest
    } = values;

    const hasBankDetails =
      bankAccount && Object.values(bankAccount).some((v) => !!v);

    const payload = {
      ...rest,
      email: rest.email || undefined,
      whatsapp: rest.whatsapp || undefined,
      gstNumber: rest.gstNumber || undefined,
      panNumber: rest.panNumber || undefined,
      fssaiNumber: rest.fssaiNumber || undefined,
      bankAccount: hasBankDetails ? bankAccount : undefined,
    } as CreateBusinessInput;

    let businessId = createdId;
    let businessName = payload.businessName;

    try {
      if (!businessId) {
        const b = await create.mutateAsync(payload);
        businessId = b.id;
        businessName = b.businessName;
        setCreatedId(b.id);
      } else {
        const b = await update.mutateAsync({
          id: businessId,
          data: payload as any,
        });
        businessName = b.businessName;
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save business");
      return;
    }

    let uploadFailed = false;
    if (pendingPhotos.length) {
      try {
        const uploaded = await uploadPhotos.mutateAsync({
          id: businessId!,
          files: pendingPhotos.map((p) => p.file as File),
        });
        const coverIdx = pendingPhotos.findIndex((p) => p.isCover);
        if (coverIdx >= 0 && uploaded[coverIdx]) {
          await setCover.mutateAsync({
            id: businessId!,
            photoId: uploaded[coverIdx].id,
          });
        }
      } catch (e: any) {
        uploadFailed = true;
        toast.error(
          `Business saved but photo upload failed: ${e?.message ?? "unknown error"}`,
        );
      }
    }

    if (pendingDocuments.length) {
      try {
        await uploadDocs.mutateAsync({
          id: businessId!,
          files: pendingDocuments.map((d) => d.file as File),
          metadata: pendingDocuments.map((d) => d.meta),
        });
      } catch (e: any) {
        uploadFailed = true;
        toast.error(
          `Business saved but document upload failed: ${e?.message ?? "unknown error"}`,
        );
      }
    }

    if (!uploadFailed) toast.success(`Saved ${businessName}`);
    navigate("/businesses");
  });

  const pending =
    create.isPending ||
    update.isPending ||
    uploadPhotos.isPending ||
    uploadDocs.isPending;
  const progress = ((step + 1) / STEP_LABELS.length) * 100;

  return (
    <AppLayout title="New business">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>
              Step {step + 1} / {STEP_LABELS.length}
            </span>
            <span className="font-medium text-foreground">
              {STEP_LABELS[step]}
            </span>
          </div>
          <div className="h-1 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <FormProvider {...form}>
          <form
            onSubmit={onSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isLast) {
                const target = e.target as HTMLElement;
                if (target.tagName !== "TEXTAREA") e.preventDefault();
              }
            }}
          >
            <div className="bg-card rounded-card border border-border p-6 mb-4">
              {step === 0 && <OwnerStep />}
              {step === 1 && <BasicsStep />}
              {step === 2 && <LocationStep />}
              {step === 3 && <FacilityStep />}
              {step === 4 && <ImagesStep />}
              {step === 5 && <ComplianceStep />}
            </div>

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
              >
                Back
              </Button>
              {!isLast ? (
                <Button type="button" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button type="submit" disabled={pending}>
                  {pending
                    ? createdId
                      ? "Saving…"
                      : "Creating…"
                    : createdId
                      ? "Save changes"
                      : "Create business"}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </AppLayout>
  );
};

export default BusinessCreateWizard;
